import type { PlatformBridge } from "@acto/taskpane/core/platform/messaging/bridge";
import type { PlatformPlugin } from "@acto/taskpane/core/platform/PlatformPlugin";
import type { PlatformEnv } from "@acto/taskpane/core/platform/runtime/env";

interface DynamicsEntityData {
  entity: string;
  id: string;
  emailAddress?: string | null;
  email?: string | null;
  emailaddress1?: string | null;
  displayName?: string | null;
  name?: string | null;
  [key: string]: unknown;
}

interface DynamicsEntityMessage {
  type: "entity";
  version: number;
  timestamp: number;
  data: DynamicsEntityData;
}

const CONTEXT_REQUEST_TYPE = "acto.requestDynamicsContext" as const;
let listenerRegistered = false;

function isDynamicsEntityMessage(value: unknown): value is DynamicsEntityMessage {
  if (!value || typeof value !== "object") return false;

  const msg = value as Partial<DynamicsEntityMessage>;

  return (
    msg.type === "entity" &&
    typeof msg.version === "number" &&
    typeof msg.timestamp === "number" &&
    !!msg.data &&
    typeof (msg.data as DynamicsEntityData).entity === "string" &&
    typeof (msg.data as DynamicsEntityData).id === "string"
  );
}

const DynamicsIframePlugin: PlatformPlugin<void> = {
  name: "dynamicsIframe",

  async canBoot(env: PlatformEnv) {
    return env.platform?.toLowerCase() === "dynamics";
  },

  async boot(_env: PlatformEnv) {
    void _env;
  },

  registerMessaging(bridge: PlatformBridge) {
    if (typeof window === "undefined") return;

    if (listenerRegistered) {
      return;
    }

    const handler = (event: MessageEvent<unknown>) => {
      if (!isDynamicsEntityMessage(event.data)) return;

      const { id, emailAddress, email, emailaddress1 } = event.data.data;
      const resolvedEmail = [emailAddress, email, emailaddress1].find(
        (value): value is string => typeof value === "string" && value.length > 0,
      );
      const normalizedEmail = resolvedEmail?.toLowerCase();
      const normalizedId = id.toLowerCase();

      bridge.dispatch({ type: "crm.entity", payload: { crmId: normalizedId, emailAddress: normalizedEmail } });
    };

    listenerRegistered = true;
    window.addEventListener("message", handler);

    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: CONTEXT_REQUEST_TYPE }, "*");
    }
  },
};

export default DynamicsIframePlugin;
